import React, { memo } from 'react';
import { camelize } from 'humps';
import classnames from 'classnames';

import { Spinner } from '../../helpers/Spinner';

import style from '../style.module.css';

const formatAverage = average => (average != null ? Number(average).toFixed(2) : '-');

const RoleLevelBenchmarksTableComponent = ({ benchmarks, isLoading, isEditState, isSuperuser, onChange }) => {
  const columns = benchmarks?.get('roleColumns');
  const listsColumn = columns?.find(column => column.get('key') === 'lists');
  const approachesColumns = columns?.filter(column => column.get('key').startsWith('approaches'));
  const restColumns = columns?.filter(
    column => column.get('key') !== 'lists' && !column.get('key').startsWith('approaches'),
  );
  const callsColumn = restColumns?.first();
  const leadProgressionColumns = restColumns?.skip(1);
  const sortedColumns = listsColumn ? [listsColumn, ...(approachesColumns ?? []), ...(restColumns ?? [])] : columns;

  const renderRow = role => {
    const id = role.get('titleId');

    return (
      <tr key={id} className="text-bold name-column">
        <td>{role.get('roleTitle')}</td>

        {sortedColumns.map(column => {
          const metricKey = camelize(column.get('key'));
          const metric = role.getIn(['metrics', metricKey]);
          const benchmark = metric?.get('benchmark');
          const benchmarkInput = (
            <input
              className={style.cellInputValue}
              disabled={!isEditState}
              min="0"
              onChange={event => onChange(id, column.get('key'), event.target.value)}
              step="1"
              type="number"
              value={benchmark ?? '0'}
            />
          );

          return (
            <td key={column.get('key')} className="text-right">
              {isSuperuser ? (
                <div className={style.cell}>
                  {benchmarkInput}
                  <div className="label label-muted font-size-10">avg {formatAverage(metric?.get('avg'))}</div>
                </div>
              ) : (
                <div className={classnames(style.cell, style.cellCentered)}>
                  <div className="label label-light-blue font-size-10">
                    avg {formatAverage(metric?.get('firmWideBenchmark'))}
                  </div>
                  {benchmarkInput}
                  <div className="label label-info font-size-10">avg {formatAverage(metric?.get('moduleAvg'))}</div>
                </div>
              )}
            </td>
          );
        })}
      </tr>
    );
  };

  return (
    <div className="position-relative">
      {isLoading && <Spinner />}
      <div className="table-responsive">
        <table className="table table-blue table-hover mb0">
          <thead>
            <tr>
              <th rowSpan="3">Role</th>
              <th colSpan="5">
                Outreach
                <span className="ml5 label label-primary font-size-10">Top of funnel</span>
              </th>
              <th colSpan="4" rowSpan="2">
                Lead Progression
                <span className="ml5 label label-info font-size-10">Conversion</span>
              </th>
            </tr>
            <tr className="col-headers">
              <th className="outreach-spacer" />
              <th className="approaches" colSpan="3">
                Approaches
              </th>
              <th className="outreach-spacer" />
            </tr>
            <tr className="col-headers">
              {listsColumn && (
                <th key={listsColumn.get('key')} title={listsColumn.get('title')}>
                  {listsColumn.get('label')}
                </th>
              )}
              {approachesColumns?.map(column => (
                <th key={column.get('key')} title={column.get('title')}>
                  {column.get('label')}
                </th>
              ))}
              {callsColumn && (
                <th key={callsColumn.get('key')} title={callsColumn.get('title')}>
                  {callsColumn.get('label')}
                </th>
              )}
              {leadProgressionColumns?.map(column => (
                <th key={column.get('key')} title={column.get('title')}>
                  {column.get('label')}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>{benchmarks?.get('roles')?.map(role => renderRow(role))}</tbody>
        </table>
      </div>
    </div>
  );
};

export const RoleLevelBenchmarksTable = memo(RoleLevelBenchmarksTableComponent);
